import { useLazyQuery, useQuery } from '@apollo/client';
import { HelpOutline } from '@mui/icons-material';
import {
  Autocomplete,
  FormControl,
  FormLabel,
  Grid,
  Icon,
  MenuItem,
  Select,
  TextField,
  Tooltip
} from '@mui/material';
import {
  CombinedSemester,
  CreateEvent,
  DistributionStatus,
  OrganizerType
} from '@thro-types/events';

import { Field, useFormikContext } from 'formik';
import { intersection } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import AUTHOR_ROLE_QUERY from 'gql/queries/EventService/getPermissions.gql';
import PERMISSION_QUERY from 'gql/queries/PermissionProvider/getPermission.gql';

import { DefaultAccordionEnum, GQLGuard } from 'app/components';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { RoleCard } from 'app/containers/Articles/Article/components/RoleCard';
import SemesterIcon from 'app/containers/Articles/Article/components/SemesterIcon';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { PermissionRole } from 'types/AppTypes';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { EventAccordion } from './EventAccordion';
import { EventUserAutcomplete } from './EventUserAutocomplete';

export const EventOrganization = () => {
  const { t } = useTranslation();
  const { setFieldValue, values } = useFormikContext<
    CreateEvent & { distributionStatus: DistributionStatus; id: number }
  >();
  const { authorRoles: authors } = useAuthContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userHasSelectedAuthorRole, setUserHasSelectedAuthorRole] = useState(false);

  const [getPermission, getPermissionQuery] = useLazyQuery(PERMISSION_QUERY, {
    fetchPolicy: 'no-cache'
  });

  const getAuthorRolesQuery = useQuery(AUTHOR_ROLE_QUERY, { variables: { limit: 0 } });

  const dataPermission = getPermissionQuery.data;

  const distributed =
    values.distributionStatus === DistributionStatus.DISTRIBUTED ||
    values.distributionStatus === DistributionStatus.DISTRIBUTING
      ? true
      : false;

  useEffect(() => {
    if (values?.hostId !== undefined) {
      const authorPermissionId = authors.find(author => parseInt(author.id, 10) === values.hostId)
        ?.authorPermission.id;
      if (authorPermissionId !== undefined) {
        getPermission({
          variables: { id: authorPermissionId }
        });
        setUserHasSelectedAuthorRole(true);
      }
    }
  }, [authors, getPermission, values.hostId]);

  const containsStudentEntitlement = (): boolean => {
    return (
      values.participantCriteria.entitlements?.length > 0 &&
      !!values.participantCriteria.entitlements.find(entitlement =>
        entitlement.includes('Studenten')
      )
    );
  };

  return (
    <GQLGuard
      query={[getAuthorRolesQuery]}
      lazyQuery={getPermissionQuery}
      onError={error => {
        dispatch(AlertActions.error(`${t(translations.pages.events.alerts.errorLoading())}`));
        navigate('/events', { replace: true });
        return null;
      }}
    >
      {([authorRoles]) => {
        const authorOrganizerOptions = authorRoles.permissions?.map(role => ({
          label: role.authorRole.name,
          id: parseInt(role.authorRole.id, 10)
        }));
        return (
          <Grid container className="event-modal__input" spacing={1}>
            {((values.id && userHasSelectedAuthorRole) || !values.id) && (
              <>
                <Grid item xs={12}>
                  <FormControl required fullWidth disabled={distributed}>
                    <FormLabel>{t(translations.pages.events.create.authorRole())}</FormLabel>
                    <Field
                      as={Select}
                      value={values.hostId || ''}
                      id="hostId"
                      name="hostId"
                      fullWidth
                      onChange={async e => {
                        await Promise.all([
                          setFieldValue('participantCriteria', {
                            entitlements: [],
                            combinedSemesters: []
                          }),
                          setFieldValue('hostId', e.target.value)
                        ]);
                      }}
                    >
                      {authors.map(author => (
                        <MenuItem key={author.id} value={parseInt(author.id, 10)}>
                          {author.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  {dataPermission === undefined ||
                  dataPermission.permission?.recipientRoles?.length === 0 ? (
                    <Grid container style={{ padding: '1em 0.5em' }}>
                      <NoItems
                        icon={<Icon className="fas fa-info-circle" />}
                        message={
                          dataPermission === undefined
                            ? t(translations.pages.articles.create.userGroup.noAuthorSelected())
                            : t(translations.pages.articles.create.userGroup.noRecipients())
                        }
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <FormControl required fullWidth disabled={distributed}>
                        <FormLabel>
                          {t(translations.pages.events.create.groupCard.participants())}
                        </FormLabel>
                        <ParticipantCriteria
                          dataPermission={dataPermission}
                          distributed={distributed}
                        />
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={12}>
                  {dataPermission !== undefined && containsStudentEntitlement() && (
                    <SemesterCriteria distributed={distributed} />
                  )}
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Grid container>
                  <FormLabel>{t(translations.pages.events.create.organizer())}</FormLabel>
                  <Grid
                    item
                    container
                    xs
                    justifyContent="flex-end"
                    alignItems="self-end"
                    paddingBottom={'0.5em'}
                  >
                    <Tooltip title={t(translations.pages.events.tooltips.organizingRoles())}>
                      <HelpOutline fontSize="medium" />
                    </Tooltip>
                  </Grid>
                </Grid>
                <Autocomplete
                  value={
                    values.organizers &&
                    values.organizers
                      .filter(org => org.organizerType !== OrganizerType.USER)
                      .map(org => authorOrganizerOptions.find(opt => opt.id === org.refId))
                  }
                  multiple
                  id="organizers"
                  options={authorOrganizerOptions}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  fullWidth
                  onChange={(_, value) => {
                    setFieldValue('organizers', [
                      ...values.organizers.filter(org => org.organizerType !== OrganizerType.ROLE),
                      ...value.map(v => ({
                        refId: v.id,
                        organizerType: OrganizerType.ROLE
                      }))
                    ]);
                  }}
                  renderInput={params => <TextField {...params} />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel>{t(translations.pages.events.create.organizingPersons())}</FormLabel>
                <EventUserAutcomplete />
              </FormControl>
            </Grid>
          </Grid>
        );
      }}
    </GQLGuard>
  );
};

const ParticipantCriteria = ({
  dataPermission,
  distributed
}: {
  dataPermission: { permission: { recipientRoles: PermissionRole[] } };
  distributed: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<CreateEvent>();

  function handleClickRecipient(participant) {
    const entitlements = values.participantCriteria.entitlements;

    if (entitlements.find(entitlement => entitlement === participant.entitlement)) {
      setFieldValue('participantCriteria.entitlements', [
        ...entitlements.filter(entitlement => entitlement !== participant.entitlement)
      ]);
    } else {
      setFieldValue('participantCriteria.entitlements', [...entitlements, participant.entitlement]);
    }
  }
  if (dataPermission?.permission.recipientRoles.length === 0) {
    return null;
  }
  return (
    <>
      {dataPermission?.permission.recipientRoles.map((roleRecipient, index) => {
        const intersectionOfEntitlementsLength = intersection(
          values.participantCriteria.entitlements,
          roleRecipient.groupEntitlements.map(ent => ent.entitlement)
        ).length;
        return (
          <Grid key={`role_${index}`} item xs={12}>
            <EventAccordion
              title={roleRecipient.name}
              elements={roleRecipient.groupEntitlements}
              type={DefaultAccordionEnum.RECIPIENT}
              amountSelected={intersectionOfEntitlementsLength}
              checked={
                intersectionOfEntitlementsLength === roleRecipient.groupEntitlements.length &&
                values.participantCriteria.entitlements.length !== 0
              }
              icon={
                <Icon
                  className={roleRecipient.icon}
                  style={{
                    width: '45px',
                    marginRight: '0.25em',
                    textAlign: 'center',
                    color: intersectionOfEntitlementsLength > 0 ? '#f39100' : 'grey'
                  }}
                />
              }
              active={false}
            >
              <Grid container>
                {roleRecipient.groupEntitlements.map((recipient, index) => (
                  <RoleCard
                    key={`rcard_${index}`}
                    object={{ name: recipient.entitlement, icon: roleRecipient.icon }}
                    selected={
                      values.participantCriteria.entitlements.find(
                        entitlement => entitlement === recipient.entitlement
                      )
                        ? true
                        : false
                    }
                    type="recipient"
                    onClick={() => {
                      handleClickRecipient(recipient);
                    }}
                    disabled={distributed}
                  />
                ))}
              </Grid>
            </EventAccordion>
          </Grid>
        );
      })}
    </>
  );
};

interface SemesterCriteriaProps {
  distributed: boolean;
}

const SemesterCriteria = (props: SemesterCriteriaProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<CreateEvent>();

  function handleClickSemester(semester) {
    if (values.participantCriteria.combinedSemesters.includes(semester)) {
      setFieldValue('participantCriteria.combinedSemesters', [
        ...values.participantCriteria.combinedSemesters.filter(sem => sem !== semester)
      ]);
    } else {
      setFieldValue('participantCriteria.combinedSemesters', [
        ...values.participantCriteria.combinedSemesters,
        semester
      ]);
    }
  }

  const semesterOptions = [
    {
      degree: 'Bachelor',
      options: [
        { label: 'Bachelor-1', value: CombinedSemester.B1 },
        { label: 'Bachelor-2', value: CombinedSemester.B2 },
        { label: 'Bachelor-3', value: CombinedSemester.B3 },
        { label: 'Bachelor-4', value: CombinedSemester.B4 },
        { label: 'Bachelor-5', value: CombinedSemester.B5 },
        { label: 'Bachelor-6', value: CombinedSemester.B6 },
        { label: 'Bachelor-7', value: CombinedSemester.B7 },
        { label: 'Bachelor-8', value: CombinedSemester.B8 },
        { label: 'Bachelor-9', value: CombinedSemester.B9 }
      ]
    },
    {
      degree: 'Master',
      options: [
        { label: 'Master-1', value: CombinedSemester.M1 },
        { label: 'Master-2', value: CombinedSemester.M2 },
        { label: 'Master-3', value: CombinedSemester.M3 }
      ]
    }
  ];
  return (
    <FormControl required fullWidth disabled={props.distributed}>
      <FormLabel>{t(translations.pages.articles.create.userGroup.semester())}</FormLabel>
      {/* {errors.semesters && <FormError message={errors.semesters} />} */}
      <Grid container>
        {semesterOptions.map((degree, index) => {
          const semesterIntersection = intersection(
            values.participantCriteria.combinedSemesters,
            degree.options.map(option => option.value)
          ).length;
          return (
            <Grid key={`semester_${index}`} item xs={12}>
              <EventAccordion
                title={degree.degree}
                elements={degree.options}
                type={DefaultAccordionEnum.SEMESTER}
                amountSelected={semesterIntersection}
                checked={
                  values.participantCriteria.combinedSemesters.length !== 0 &&
                  semesterIntersection >= degree.options.length
                }
                icon={
                  <SemesterIcon
                    header
                    type={degree.degree === 'Master' ? 'master' : 'bachelor'}
                    active={semesterIntersection > 0}
                  />
                }
                active={semesterIntersection > 0}
              >
                <Grid container>
                  {degree.options.map((semester, index) => (
                    <RoleCard
                      key={`scard_${index}`}
                      object={{ name: semester.label, semester: semester.label.split('-')[1] }}
                      selected={
                        values.participantCriteria.combinedSemesters.includes(semester.value)
                          ? true
                          : false
                      }
                      type="semester"
                      onClick={() => {
                        handleClickSemester(semester.value);
                      }}
                      disabled={props.distributed}
                    />
                  ))}
                </Grid>
              </EventAccordion>
            </Grid>
          );
        })}
      </Grid>
    </FormControl>
  );
};
